// Typed client for the public patient portal.
//
// Every call passes `auth: false`. That is not cosmetic: the shared handler in
// lib/api.ts turns a 401 into a logout and a hard redirect to /login, which on
// this page would throw a patient who followed their own link onto a staff
// sign-in screen. There is no session here to lose, so the interceptor has
// nothing useful to do and everything to break.

import { api, ApiError } from '@/lib/api';
import type {
  PatientPortalView,
  PatientPortalViewDTO,
  PortalLabOrder,
  PortalLabOrderDTO,
  PortalLabTest,
  PortalLabTestDTO,
  PortalReportUrl,
} from '@/types/patientPortal';

function toDate(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new ApiError('PARSE_ERROR', `Invalid timestamp from server: ${iso}`, 0);
  }
  return d;
}

function toDateOrNull(iso: string | null): Date | null {
  return iso === null ? null : toDate(iso);
}

function parseTest(dto: PortalLabTestDTO): PortalLabTest {
  return { ...dto, resultedAt: toDateOrNull(dto.resultedAt) };
}

function parseOrder(dto: PortalLabOrderDTO): PortalLabOrder {
  return {
    ...dto,
    orderedOn: toDate(dto.orderedOn),
    collectedAt: toDateOrNull(dto.collectedAt),
    tests: dto.tests.map(parseTest),
  };
}

export function parsePortalView(dto: PatientPortalViewDTO): PatientPortalView {
  return { ...dto, labs: dto.labs.map(parseOrder) };
}

export const patientPortalApi = {
  get: async (portalToken: string): Promise<PatientPortalView> =>
    parsePortalView(
      await api.get<PatientPortalViewDTO>(
        `/api/public/patient/${encodeURIComponent(portalToken)}`,
        { auth: false },
      ),
    ),

  reportUrl: (portalToken: string, documentId: string) =>
    api.get<PortalReportUrl>(
      `/api/public/patient/${encodeURIComponent(portalToken)}/documents/${encodeURIComponent(
        documentId,
      )}/url`,
      { auth: false },
    ),
};
